<template>
  <div class="design-page">
    <div class="content-header">
      <div class="design-title">{{ $t('AccountInfo.DesignBill.designEarnings') }}</div>
      <div class="design-btn"
           @click="showDetail">
        <FileSearchOutlined class="design-icon" />
        {{ $t('AccountInfo.DesignBill.viewAll') }}
      </div>
    </div>
    <div class="design-content">
      <a-empty v-if="dataInit.length == 0"
               :image="simpleImage"
               class="empty-content" />
      <div v-for="item in dataInit"
           :key="item.id">
        <div class="flex">
          <div style="display: flex;align-items: center;">
            <div v-if="item.status == 1"
                 class="design-price">+</div>
            <div class="design-price weight"
                 :class="item.status == 1 ? '' : 'design-forzen'">{{ item.amount }}</div>
            <div v-if="item.status == 2"
                 class="design-price-frozen">{{ $t('AccountInfo.DesignBill.unrecorded') }}</div>
            <div v-if="item.status == 3"
                 class="design-price-frozen">{{ $t('AccountInfo.DesignBill.refunded') }}</div>
          </div>
          <div class="design-time">{{ dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss') }}</div>
        </div>
        <div class="design-divider"></div>
      </div>
    </div>
    <a-modal v-model:visible="isVisible"
             :title="$t('AccountInfo.DesignBill.designEarnings')"
             width="40%"
             @ok="handleOk"
             @cancel="handleOk">
      <a-scroll-table class="table"
                      :columns="FUND_DETAILS_COLUMNS"
                      :data-source="dataSource"
                      :pagination="false">
        <template #bodyCell="{ record, column }">
          <template v-if="column.dataIndex === 'amount'">
            <div class="table-amount">
              <div v-if="record.status == 2"
                   class="design-price-frozen">{{ $t('AccountInfo.DesignBill.unrecorded') }}</div>
              <div class="design-price"
                   :class="record.status == 1 ? '' : 'design-forzen'">{{ record.amount }}</div>
            </div>
          </template>
          <template v-if="column.dataIndex === 'createdAt'">
            <div>{{ dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss') }}</div>
          </template>
        </template>
      </a-scroll-table>
      <div class="footer">
        <APagination v-model:current="pagination.current"
                     v-model:pageSize="pagination.pageSize"
                     :show-size-changer="true"
                     :total="pagination.total"
                     @change="handleTableChange"
                     :show-total="showTotal" />
      </div>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AXIOSKEY
} from '@/core'
import { AScrollTable, APagination } from '@iota-cn/ui-components'
import type { TableColumnsType } from 'ant-design-vue'
import dayjs from 'dayjs'
import { computed, inject, onMounted, ref } from 'vue'
import { Empty } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE

interface DataSource {
  id: number,
  number: string,
  out_trade_number: string,
  amount: number,
  createdAt: number,
  type: number,
  status: number,
  user: {
    id: string,
    usernames: string
  }
}
const dataSource = ref<DataSource[]>([])
const dataInit = ref<DataSource[]>([])
const axios = inject(AXIOSKEY)

const isVisible = ref<boolean>(false)
const FUND_DETAILS_COLUMNS = computed<TableColumnsType>(() => [
  {
    title: t('AccountInfo.DesignBill.earningsAmount'),
    dataIndex: 'amount',
    ellipsis: true,
    align: 'center',
  },
  {
    title: t('AccountInfo.DesignBill.purchaseTime'),
    dataIndex: 'createdAt',
    ellipsis: true,
    align: 'center',
  },
])
const handleOk = () => {
  isVisible.value = false
}
const pagination = ref({
  current: 1,
  total: 0,
  offset: 0,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'], // 每页中显示的数据
  showTotal: (total: number) => `${t('AccountInfo.DesignBill.totalRecords')} ${total} ${t('AccountInfo.DesignBill.records')}`, // 分页中显示总的数据
  size: 'small',
})

const showTotal = (total: number) => `${t('AccountInfo.DesignBill.totalRecords')} ${total} ${t('AccountInfo.DesignBill.records')}`

onMounted(async () => {
  await getBmsBill()
  dataInit.value = [...dataSource.value]
})

const getBmsBill = async () => {
  await axios?.get(`/v1/api/bms/bill_design?limit=${pagination.value.pageSize}&offset=${pagination.value.offset}`).then((res: any) => {
    dataSource.value = res.billItem
    pagination.value.total = res.total
  })
}
const showDetail = () => {
  isVisible.value = true
}

const handleTableChange = (current: number, pageSize: number) => {
  pagination.value.current = current
  pagination.value.offset = (current - 1) * pageSize
  getBmsBill()
}

</script>

<style lang="less" scoped>
.design-page {
  width: 378px;
  height: calc(100% - 230px);
  margin-bottom: 20px;
  overflow: hidden;

  .content-header {
    height: 30px;
    line-height: 30px;
    display: flex;
    justify-content: space-between;
    padding: 0 20px;

    .design-title {
      font-weight: bold;
      font-size: 16px;
      color: #111;
    }

    .design-btn {
      cursor: pointer;

      .design-icon {
        margin-right: 5px;
      }
    }
  }

  .design-content {
    height: calc(100% - 60px);
    overflow: auto;
    padding: 20px;
    padding-top: 0;

    .empty-content {
      margin-top: 100px;
    }

    .flex {
      height: 40px;
      line-height: 40px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 10px 0;
    }


    .design-time {
      color: rgba(153, 153, 153, 1);
    }

    .design-divider {
      height: 1px;
      width: 100%;
      background-color: #eee;
    }
  }

  .weight {
    font-weight: bold;
  }
}

.table {
  flex: 1;
  height: 500px;
  overflow: hidden;

  .table-amount {
    display: flex;
    width: 120px;
    margin: auto;
    justify-content: center;
  }
}

.footer {
  display: flex;
  justify-content: end;
  margin-top: 10px;
}

.design-price {
  color: #23C432;
  font-size: 16px;
}

.design-forzen {
  color: rgba(153, 153, 153, 1)
}

.design-price-frozen {
  background-color: #f6f6f6;
  width: 56px;
  height: 24px;
  text-align: center;
  color: rgba(153, 153, 153, 1);
  margin: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
